// #70 Diff (DC-21, one diff): `inline` shows the original struck in --text-3 → the revised value in --text, the
// reason in --text-2, actor · time, and a *1 of n* stepper whose two buttons are named by the revision they show.
// `cols` is the two-column form, Old | New (or Original | Revised for an own-score revision), the changed value on
// --t-gold, a Rationale line under it; `locked` marks the left column read-only (D: the lock glyph and his rule
// "Audit all original/revised values." as its description). No red or green tint: those keep their verdict duties.
// G8's DiffRevise is the editable `.revise` form.

import type { ReactNode } from "react";
import { IconButton } from "../core/Button";
import { Icon } from "../core/icons";
import { KeyValue } from "../core/KeyValue";
import { Table, Td, Th, Tr } from "../core/Table";
import { cx } from "../lib/cx";
import { formatDateTime, toEpoch, type Instant } from "../lib/time";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { DIFF_LABELS, DIFF_TEMPLATES } from "./labels";
import type { Hs } from "../lib/hs";

export interface DiffStepper {
  /** The revision shown, 1-based. */
  index: number;
  count: number;
  /** Shows another revision (1-based); the stepper wraps. Not drawn below two revisions. */
  onStep: (index: number) => void;
  /** The back (previous) button's hotspot. */
  hs?: Hs | undefined;
  /** The forward (next) button's hotspot. */
  nextHs?: Hs | undefined;
}

export interface DiffRow {
  /** The field's name, from a labels module. */
  label: string;
  old: ReactNode;
  new: ReactNode;
  /** Default: the two values differ (compared when both are strings or numbers). */
  changed?: boolean | undefined;
}

interface DiffCommon {
  className?: string | undefined;
}

export interface DiffInlineProps extends DiffCommon {
  form?: "inline" | undefined;
  original: ReactNode;
  revised: ReactNode;
  reason?: ReactNode | undefined;
  actor?: ReactNode | undefined;
  at?: Instant | undefined;
  timeZone?: string | undefined;
  step?: DiffStepper | undefined;
}

export interface DiffColsProps extends DiffCommon {
  form: "cols";
  rows: readonly DiffRow[];
  /** "oldNew" (default) or "originalRevised" (an own-score revision). */
  heads?: "oldNew" | "originalRevised" | undefined;
  rationale?: ReactNode | undefined;
  /** The left column is read-only. */
  locked?: boolean | undefined;
  /** The table's name (default *Original/revised values*). */
  label?: string | undefined;
}

export type DiffProps = DiffInlineProps | DiffColsProps;

function Stepper({ step }: { step: DiffStepper }) {
  const { index, count, onStep } = step;
  const prev = index <= 1 ? count : index - 1;
  const next = index >= count ? 1 : index + 1;
  return (
    <span className="mc-diff-step">
      <IconButton sm icon="back" label={DIFF_TEMPLATES.stepOf({ i: prev, n: count })} onClick={() => onStep(prev)} hs={step.hs} />
      <span className="mc-diff-pos" aria-live="polite">{DIFF_TEMPLATES.stepOf({ i: index, n: count })}</span>
      <IconButton sm icon="fwd" label={DIFF_TEMPLATES.stepOf({ i: next, n: count })} onClick={() => onStep(next)} hs={step.nextHs} />
    </span>
  );
}

function same(a: ReactNode, b: ReactNode): boolean {
  return (typeof a === "string" || typeof a === "number") && (typeof b === "string" || typeof b === "number") ? String(a) === String(b) : false;
}

function DiffCols({ rows, heads = "oldNew", rationale, locked, label = DIFF_LABELS.originalRevisedValues, className }: DiffColsProps) {
  const left = heads === "originalRevised" ? DIFF_LABELS.original : DIFF_LABELS.old;
  const right = heads === "originalRevised" ? DIFF_LABELS.revised : DIFF_LABELS.new;
  return (
    <div className={cx("mc-diff", "mc-diff-cols", locked && "mc-diff-locked", className)}>
      <Table
        label={label}
        head={(
          <Tr>
            <Th><VisuallyHidden>{label}</VisuallyHidden></Th>
            <Th>
              <span className="mc-diff-h">
                {locked ? <Icon name="lock" size={14} /> : null}
                {left}
                {locked ? <VisuallyHidden>{DIFF_LABELS.auditOriginalRevised}</VisuallyHidden> : null}
              </span>
            </Th>
            <Th>{right}</Th>
          </Tr>
        )}
      >
        {rows.map((row, index) => {
          const changed = row.changed ?? !same(row.old, row.new);
          return (
            <Tr key={`${row.label}-${index}`}>
              <Th row>{row.label}</Th>
              <Td label={left} className="mc-diff-o">{row.old}</Td>
              <Td label={right} className={cx("mc-diff-n", changed && "mc-diff-ch")}>{row.new}</Td>
            </Tr>
          );
        })}
      </Table>
      {rationale !== undefined && rationale !== null ? <KeyValue rows items={[[DIFF_LABELS.rationale, rationale]]} /> : null}
    </div>
  );
}

export function Diff(props: DiffProps) {
  if (props.form === "cols") return <DiffCols {...props} />;
  const { original, revised, reason, actor, at, timeZone, className } = props;
  const step = props.step && props.step.count >= 2 ? props.step : undefined;
  return (
    <div className={cx("mc-diff", "mc-diff-inline", className)}>
      <p className="mc-diff-v">
        <del className="mc-diff-was"><VisuallyHidden>{DIFF_LABELS.original}</VisuallyHidden>{original}</del>
        <Icon name="right" size={14} />
        <ins className="mc-diff-now"><VisuallyHidden>{DIFF_LABELS.revised}</VisuallyHidden>{revised}</ins>
      </p>
      {reason !== undefined && reason !== null ? (
        <p className="mc-diff-r"><span className="mc-diff-k">{DIFF_LABELS.reason}</span>{reason}</p>
      ) : null}
      {(actor !== undefined && actor !== null) || at !== undefined || step ? (
        <p className="mc-diff-m">
          {actor !== undefined && actor !== null ? <span>{actor}</span> : null}
          {at !== undefined ? <time dateTime={new Date(toEpoch(at)).toISOString()}>{formatDateTime(at, timeZone)}</time> : null}
          {step ? <Stepper step={step} /> : null}
        </p>
      ) : null}
    </div>
  );
}
